/**
 * Seule API de donnees que l'UI a le droit d'appeler.
 *
 * Elle tient les invariants que le schema ne peut pas exprimer :
 *
 *  1. le tiers « moi » existe toujours, en un seul exemplaire, et ne se
 *     supprime pas — toute la repartition lui attribue le reliquat ;
 *  2. supprimer une operation supprime ses parts, les reglements de ces parts,
 *     ses pieces jointes et l'appariement a son echeance : aucun orphelin ne
 *     doit subsister pour fausser un bilan ou un justificatif ;
 *  3. on ne supprime pas un compte, une categorie ou un tiers encore utilise —
 *     on l'archive. Effacer une categorie ferait disparaitre des depenses de
 *     toutes les statistiques sans que rien ne le signale ;
 *  4. appliquer un modele **copie** ses parts (voir `partsDepuisModele`).
 */

import type { Donnees } from '../analysis/donnees'
import { partsDepuisModele } from '../analysis/repartition'
import { aujourdhui, nowInstant } from '../domain/temps'
import {
  REGLAGES_DEFAUT,
  type Budget,
  type Categorie,
  type Compte,
  type DayKey,
  type Id,
  type ModeleRepartition,
  type Operation,
  type Part,
  type Recurrence,
  type Reglages,
  type Reglement,
  type Tiers,
} from '../domain/types'
import { META_INITIALISEE, META_MOI, META_REGLAGES, db, ecrireMeta, lireMeta, nouvelId } from './db'

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

const CATEGORIES_INITIALES: Array<{ nom: string; sens: 'depense' | 'revenu'; couleur: string; enfants?: string[] }> = [
  { nom: 'Logement', sens: 'depense', couleur: '#4c8bf5', enfants: ['Loyer', 'Charges', 'Énergie'] },
  { nom: 'Alimentation', sens: 'depense', couleur: '#2fbf71' },
  { nom: 'Santé', sens: 'depense', couleur: '#e5484d', enfants: ['Primes LAMal', 'Médecin', 'Dentiste', 'Pharmacie', 'Lunettes'] },
  { nom: 'Assurances', sens: 'depense', couleur: '#8e6cef' },
  { nom: 'Transports', sens: 'depense', couleur: '#f5a524' },
  { nom: 'Enfants', sens: 'depense', couleur: '#ec6fae' },
  { nom: 'Impôts', sens: 'depense', couleur: '#6b7280' },
  { nom: 'Loisirs', sens: 'depense', couleur: '#14b8a6' },
  { nom: 'Divers', sens: 'depense', couleur: '#a3a3a3' },
  { nom: 'Salaire', sens: 'revenu', couleur: '#2fbf71' },
  { nom: 'Autres revenus', sens: 'revenu', couleur: '#4c8bf5' },
]

/**
 * Prepare une base vierge et garantit l'existence du tiers « moi ».
 *
 * Idempotent : appele a chaque demarrage. Les categories d'exemple ne sont
 * creees qu'a la toute premiere ouverture ; les supprimer ensuite ne les fait
 * pas revenir.
 */
export async function initialiser(): Promise<Id> {
  return db.transaction('rw', [db.meta, db.tiers, db.categories, db.comptes], async () => {
    let moiId = await lireMeta<Id | null>(META_MOI, null)
    if (!moiId || !(await db.tiers.get(moiId))) {
      const existant = await db.tiers.where('type').equals('moi').first()
      moiId = existant?.id ?? nouvelId()
      if (!existant) {
        await db.tiers.add({ id: moiId, nom: 'Moi', type: 'moi', adresse: '', courriel: '', archive: 0 })
      }
      await ecrireMeta(META_MOI, moiId)
    }

    if (!(await lireMeta<string | null>(META_INITIALISEE, null))) {
      for (const c of CATEGORIES_INITIALES) {
        const id = nouvelId()
        await db.categories.add({ id, nom: c.nom, sens: c.sens, parentId: null, couleur: c.couleur, archive: 0, modeleParDefautId: null })
        for (const e of c.enfants ?? []) {
          await db.categories.add({ id: nouvelId(), nom: e, sens: c.sens, parentId: id, couleur: c.couleur, archive: 0, modeleParDefautId: null })
        }
      }
      if ((await db.comptes.count()) === 0) {
        await db.comptes.add({
          id: nouvelId(),
          nom: 'Compte courant',
          type: 'courant',
          soldeInitial: 0,
          soldeInitialLe: aujourdhui(),
          couleur: '#4c8bf5',
          archive: 0,
          ordre: 0,
        })
      }
      await ecrireMeta(META_INITIALISEE, nowInstant())
    }
    return moiId
  })
}

/** Instantane complet, hors blobs des pieces jointes. */
export async function chargerDonnees(): Promise<Donnees> {
  const [comptes, categories, tiers, operations, recurrences, exceptions, modeles, parts, reglements, budgets, moiId] =
    await Promise.all([
      db.comptes.orderBy('nom').toArray(),
      db.categories.orderBy('nom').toArray(),
      db.tiers.orderBy('nom').toArray(),
      db.operations.orderBy('date').toArray(),
      db.recurrences.toArray(),
      db.exceptions.toArray(),
      db.modeles.orderBy('nom').toArray(),
      db.parts.toArray(),
      db.reglements.orderBy('date').toArray(),
      db.budgets.toArray(),
      lireMeta<Id>(META_MOI, ''),
    ])
  comptes.sort((a, b) => a.ordre - b.ordre || a.nom.localeCompare(b.nom))
  categories.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
  return { comptes, categories, tiers, operations, recurrences, exceptions, modeles, parts, reglements, budgets, moiId }
}

// ---------------------------------------------------------------------------
// Reglages
// ---------------------------------------------------------------------------

export async function lireReglages(): Promise<Reglages> {
  const stocke = await lireMeta<Partial<Reglages>>(META_REGLAGES, {})
  // Fusion avec les defauts : un reglage ajoute dans une version ulterieure ne
  // doit pas rendre `undefined` un parametre dont dependent les calculs.
  return { ...REGLAGES_DEFAUT, ...stocke }
}

export async function ecrireReglages(patch: Partial<Reglages>): Promise<Reglages> {
  const fusion = { ...(await lireReglages()), ...patch }
  await ecrireMeta(META_REGLAGES, fusion)
  return fusion
}

// ---------------------------------------------------------------------------
// Comptes
// ---------------------------------------------------------------------------

export class RefusSuppression extends Error {}

export async function enregistrerCompte(c: Omit<Compte, 'id'> & { id?: Id }): Promise<Id> {
  const id = c.id ?? nouvelId()
  await db.comptes.put({ ...c, id })
  return id
}

export async function supprimerCompte(id: Id): Promise<void> {
  const [ops, recs, regs] = await Promise.all([
    db.operations.where('compteId').equals(id).count(),
    db.recurrences.where('compteId').equals(id).count(),
    db.reglements.where('compteId').equals(id).count(),
  ])
  if (ops + recs + regs > 0) {
    throw new RefusSuppression(
      `Ce compte porte ${ops} opération(s), ${recs} récurrence(s) et ${regs} remboursement(s). Archivez-le plutôt : son historique restera dans les statistiques.`,
    )
  }
  await db.transaction('rw', [db.comptes, db.budgets], async () => {
    await db.budgets.where('compteId').equals(id).delete()
    await db.comptes.delete(id)
  })
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export async function enregistrerCategorie(c: Omit<Categorie, 'id'> & { id?: Id }): Promise<Id> {
  const id = c.id ?? nouvelId()
  if (c.parentId === id) throw new Error('Une catégorie ne peut pas être sa propre parente.')
  await db.categories.put({ ...c, id })
  return id
}

export async function supprimerCategorie(id: Id): Promise<void> {
  const [ops, recs, enfants] = await Promise.all([
    db.operations.where('categorieId').equals(id).count(),
    db.recurrences.where('categorieId').equals(id).count(),
    db.categories.where('parentId').equals(id).count(),
  ])
  if (ops + recs > 0) {
    throw new RefusSuppression(
      `Cette catégorie classe ${ops} opération(s) et ${recs} récurrence(s). Archivez-la plutôt, ou déplacez d'abord ses opérations.`,
    )
  }
  if (enfants > 0) throw new RefusSuppression('Cette catégorie a des sous-catégories : supprimez-les ou déplacez-les d’abord.')
  await db.transaction('rw', [db.categories, db.budgets], async () => {
    await db.budgets.where('categorieId').equals(id).delete()
    await db.categories.delete(id)
  })
}

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

export async function enregistrerTiers(t: Omit<Tiers, 'id'> & { id?: Id }): Promise<Id> {
  const id = t.id ?? nouvelId()
  const existant = t.id ? await db.tiers.get(t.id) : undefined
  // « moi » est unique : on ne le cree pas a la main, et on ne le transforme pas.
  if (t.type === 'moi' && existant?.type !== 'moi') throw new Error('Le tiers « moi » existe déjà.')
  if (existant?.type === 'moi' && t.type !== 'moi') throw new Error('Le tiers « moi » ne peut pas changer de type.')
  await db.tiers.put({ ...t, id })
  return id
}

export async function supprimerTiers(id: Id): Promise<void> {
  const t = await db.tiers.get(id)
  if (t?.type === 'moi') throw new RefusSuppression('Le tiers « moi » porte votre reste à charge : il ne peut pas être supprimé.')
  const parts = await db.parts.where('tiersId').equals(id).count()
  if (parts > 0) {
    throw new RefusSuppression(`Ce tiers intervient dans ${parts} répartition(s). Archivez-le plutôt.`)
  }
  const modeles = (await db.modeles.toArray()).filter((m) => m.parts.some((p) => p.tiersId === id))
  if (modeles.length > 0) {
    throw new RefusSuppression(`Ce tiers figure dans le modèle « ${modeles[0]!.nom} ». Retirez-l’en d’abord.`)
  }
  await db.tiers.delete(id)
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

export type SaisieOperation = Omit<Operation, 'id' | 'creeLe'> & { id?: Id; creeLe?: string }
export type SaisiePart = Omit<Part, 'id' | 'operationId'> & { id?: Id }

/**
 * Enregistre une operation et **remplace** sa cascade de parts.
 *
 * Les parts dont l'identifiant est conserve gardent leurs reglements : corriger
 * le montant de la caisse maladie ne doit pas effacer le versement deja recu
 * de l'ex-epouse. Seules les parts retirees de la cascade perdent les leurs.
 */
export async function enregistrerOperation(saisie: SaisieOperation, parts: readonly SaisiePart[]): Promise<Id> {
  const id = saisie.id ?? nouvelId()
  const op: Operation = { ...saisie, id, creeLe: saisie.creeLe ?? nowInstant() }
  // Un revenu ne se repartit pas.
  const partsFinales: Part[] =
    op.sens === 'depense' ? parts.map((p, i) => ({ ...p, id: p.id ?? nouvelId(), operationId: id, ordre: p.ordre ?? i + 1 })) : []

  await db.transaction('rw', [db.operations, db.parts, db.reglements], async () => {
    const anciennes = await db.parts.where('operationId').equals(id).toArray()
    const gardees = new Set(partsFinales.map((p) => p.id))
    const retirees = anciennes.filter((p) => !gardees.has(p.id)).map((p) => p.id)
    if (retirees.length) {
      await db.reglements.where('partId').anyOf(retirees).delete()
      await db.parts.bulkDelete(retirees)
    }
    await db.operations.put(op)
    if (partsFinales.length) await db.parts.bulkPut(partsFinales)
  })
  return id
}

export async function supprimerOperation(id: Id): Promise<void> {
  await db.transaction('rw', [db.operations, db.parts, db.reglements, db.pieces, db.exceptions], async () => {
    const parts = await db.parts.where('operationId').equals(id).primaryKeys()
    if (parts.length) await db.reglements.where('partId').anyOf(parts).delete()
    await db.parts.where('operationId').equals(id).delete()
    await db.pieces.where('operationId').equals(id).delete()
    // Une exception `remplacee` pointant sur cette operation n'a plus d'objet :
    // l'echeance redevient simplement prevue.
    await db.exceptions.filter((e) => e.operationId === id).delete()
    await db.operations.delete(id)
  })
}

export async function basculerPointage(id: Id): Promise<void> {
  const op = await db.operations.get(id)
  if (op) await db.operations.update(id, { pointee: op.pointee ? 0 : 1 })
}

// ---------------------------------------------------------------------------
// Recurrences et echeances
// ---------------------------------------------------------------------------

export async function enregistrerRecurrence(r: Omit<Recurrence, 'id'> & { id?: Id }): Promise<Id> {
  const id = r.id ?? nouvelId()
  await db.recurrences.put({ ...r, id, intervalle: Math.max(1, Math.floor(r.intervalle || 1)) })
  return id
}

/**
 * Supprime la regle **sans** supprimer les operations qu'elle a produites :
 * les primes deja payees restent des depenses reelles. Elles perdent seulement
 * leur lien a la regle.
 */
export async function supprimerRecurrence(id: Id): Promise<void> {
  await db.transaction('rw', [db.recurrences, db.operations, db.exceptions], async () => {
    await db.operations.where('recurrenceId').equals(id).modify({ recurrenceId: null, occurrence: null })
    await db.exceptions.where('recurrenceId').equals(id).delete()
    await db.recurrences.delete(id)
  })
}

/**
 * Transforme une echeance prevue en operation reelle, en appliquant d'office le
 * modele de repartition de la recurrence. Le montant et la date peuvent
 * differer de la regle : la prime a augmente, le prelevement a eu lieu le 6.
 */
export async function realiserEcheance(
  recurrenceId: Id,
  occurrence: DayKey,
  ajustements: Partial<Pick<Operation, 'date' | 'montant' | 'compteId' | 'note' | 'pointee'>> = {},
): Promise<Id> {
  const r = await db.recurrences.get(recurrenceId)
  if (!r) throw new Error('Récurrence introuvable.')
  const deja = await db.operations.where('[recurrenceId+occurrence]').equals([recurrenceId, occurrence]).first()
  if (deja) return deja.id

  const id = nouvelId()
  const modele = r.modeleRepartitionId ? await db.modeles.get(r.modeleRepartitionId) : undefined

  await db.transaction('rw', [db.operations, db.parts, db.exceptions], async () => {
    await db.operations.add({
      id,
      sens: r.sens,
      date: ajustements.date ?? occurrence,
      montant: ajustements.montant ?? r.montant,
      libelle: r.libelle,
      categorieId: r.categorieId,
      compteId: ajustements.compteId ?? r.compteId,
      note: ajustements.note ?? '',
      pointee: ajustements.pointee ?? 0,
      recurrenceId,
      occurrence,
      creeLe: nowInstant(),
    })
    if (modele && r.sens === 'depense') await db.parts.bulkAdd(partsDepuisModele(modele.parts, id, nouvelId))
    await db.exceptions.where('[recurrenceId+occurrence]').equals([recurrenceId, occurrence]).delete()
  })
  return id
}

export async function ignorerEcheance(recurrenceId: Id, occurrence: DayKey): Promise<void> {
  await db.transaction('rw', db.exceptions, async () => {
    await db.exceptions.where('[recurrenceId+occurrence]').equals([recurrenceId, occurrence]).delete()
    await db.exceptions.add({ id: nouvelId(), recurrenceId, occurrence, statut: 'ignoree', operationId: null })
  })
}

export async function retablirEcheance(recurrenceId: Id, occurrence: DayKey): Promise<void> {
  await db.exceptions.where('[recurrenceId+occurrence]').equals([recurrenceId, occurrence]).delete()
}

// ---------------------------------------------------------------------------
// Modeles de repartition
// ---------------------------------------------------------------------------

export async function enregistrerModele(m: Omit<ModeleRepartition, 'id'> & { id?: Id }): Promise<Id> {
  const id = m.id ?? nouvelId()
  await db.modeles.put({ ...m, id, parts: m.parts.map((p, i) => ({ ...p, ordre: p.ordre ?? i + 1 })) })
  return id
}

/** Les operations deja reparties gardent leur copie : seules les suggestions
 *  par defaut (categories, recurrences) perdent la reference. */
export async function supprimerModele(id: Id): Promise<void> {
  await db.transaction('rw', [db.modeles, db.categories, db.recurrences], async () => {
    await db.categories.where('modeleParDefautId').equals(id).modify({ modeleParDefautId: null })
    await db.recurrences.where('modeleRepartitionId').equals(id).modify({ modeleRepartitionId: null })
    await db.modeles.delete(id)
  })
}

// ---------------------------------------------------------------------------
// Reglements
// ---------------------------------------------------------------------------

export async function enregistrerReglement(r: Omit<Reglement, 'id' | 'creeLe'> & { id?: Id; creeLe?: string }): Promise<Id> {
  if (!(await db.parts.get(r.partId))) throw new Error('Part introuvable.')
  if (!Number.isInteger(r.montant) || r.montant <= 0) throw new Error('Le montant doit être positif.')
  const id = r.id ?? nouvelId()
  await db.reglements.put({ ...r, id, creeLe: r.creeLe ?? nowInstant() })
  return id
}

export async function supprimerReglement(id: Id): Promise<void> {
  await db.reglements.delete(id)
}

// ---------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------

export async function enregistrerBudget(b: Omit<Budget, 'id'> & { id?: Id }): Promise<Id> {
  const id = b.id ?? nouvelId()
  if (b.fin !== null && b.fin < b.debut) throw new Error('La fin du budget précède son début.')
  await db.budgets.put({ ...b, id })
  return id
}

export async function supprimerBudget(id: Id): Promise<void> {
  await db.budgets.delete(id)
}
